'use client';

import { motion, useInView, type Variants } from 'framer-motion';
import { useRef, type ElementType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

const motionMap = {
  div: motion.div,
  span: motion.span,
  h1: motion.h1,
  p: motion.p,
  a: motion.a,
  button: motion.button,
  figure: motion.figure,
} as const;

type MotionTag = keyof typeof motionMap;

type TimelineContentProps = {
  as?: ElementType;
  children: ReactNode;
  animationNum?: number;
  timelineRef?: React.RefObject<HTMLElement | null>;
  customVariants?: Variants;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
};

export function TimelineContent({
  as = 'div',
  children,
  animationNum = 0,
  timelineRef,
  customVariants,
  className,
  ...props
}: TimelineContentProps) {
  const localRef = useRef<HTMLElement>(null);
  const inViewRef = timelineRef ?? localRef;
  const isInView = useInView(inViewRef, { once: true, margin: '-80px' });

  const defaultVariants: Variants = {
    hidden: { opacity: 0, y: 24, filter: 'blur(8px)' },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { delay: i * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  const variants = customVariants ?? defaultVariants;
  const MotionTag = motionMap[(as as MotionTag) in motionMap ? (as as MotionTag) : 'div'];

  return (
    <MotionTag
      custom={animationNum}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants}
      className={cn(className)}
      {...props}
    >
      {children}
    </MotionTag>
  );
}
